import { InputRichText } from '@dixa/lib-shared-habla';
import { Channel } from 'lib-shared-types-channels';
import { memo, useCallback, useMemo, useState } from 'react';

import { CopilotAction, CopilotDropdown } from 'components/copilotDropdown';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { AiDraftContent } from './components/aiDraft/editorCopilotDropdownAiDraft';
import { EditorCopilotDropdownContext } from './editorCopilotDropdownContext';

export const DEFAULT_PINNED_STATE = {
  improveReply: false,
  improveReplyMatchStyleGuide: false,
  checkSpellingAndGrammar: false,
  translateToConversationLanguage: true,
  expandText: true,
};

interface EditorCopilotDropdownProps {
  editorCopilotActions: Record<string, CopilotAction>;
  channel?: Channel;
  dixaConversationId?: string;
}

/**
 * Represents a dropdown component designed to be integrated into a rich text
 * editor's toolbar as an addon, providing AI-powered functionalities to enhance
 * and refine the content within the editor.
 */
export const EditorCopilotDropdown = memo(function EditorCopilotDropdown({
  editorCopilotActions,
  channel,
  dixaConversationId,
}: EditorCopilotDropdownProps): JSX.Element | null {
  const { hasCopilot } = useCopilotTrial();

  const [cachedDraft, setCachedDraft] = useState<string | undefined>(undefined);
  const [cachedAgentId, setCachedAgentId] = useState<string | undefined>(
    undefined,
  );
  const handleDraftGenerated = useCallback(
    (text: string | undefined) => setCachedDraft(text),
    [],
  );

  const actions = useMemo<Record<string, CopilotAction>>(() => {
    if (!dixaConversationId) return editorCopilotActions;

    const aiDraftAction: CopilotAction = {
      title: 'AI agent draft',
      icon: 'chat-bot',
      hasDividerBefore: false,
      isVisible: ({ featureFlags }) => featureFlags?.mimDrafts ?? false,
      content: (
        <AiDraftContent
          dixaConversationId={dixaConversationId}
          initialDraft={cachedDraft}
          initialAgentId={cachedAgentId}
          onDraftGenerated={handleDraftGenerated}
          onAgentSelected={setCachedAgentId}
        />
      ),
    } satisfies CopilotAction;

    const entries = Object.entries(editorCopilotActions);
    return Object.fromEntries([
      ...entries.slice(0, -1),
      ['aiDraft', aiDraftAction],
      ...entries.slice(-1),
    ]);
  }, [
    editorCopilotActions,
    dixaConversationId,
    cachedDraft,
    cachedAgentId,
    handleDraftGenerated,
  ]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <EditorCopilotDropdownContext.Provider value={{ channel }}>
      <div>
        <CopilotDropdown actions={actions} fixed noArrow noStyle>
          <InputRichText.ToolbarButton icon="ai" />
        </CopilotDropdown>
      </div>
    </EditorCopilotDropdownContext.Provider>
  );
});
